import { useEffect, useState } from 'react';
import apiClient from '../../services/api';
import { showToast } from '../ui/Toast';
import { DatePicker } from '../ui/DatePicker';
import { useAuth } from '../../contexts/AuthContext';
import { isAdmin } from '../../types/auth';

interface Timesheet {
    id: string;
    user: {
        id: string;
        name: string;
        role: { name: string };
    };
    shiftDate: string;
    startTime: string;
    endTime: string;
    totalHours: number;
    breakDuration?: number;
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    overtimeReason?: string;
    rejectionReason?: string;
    notes?: string;
    approvedAt?: string;
    approvedByRole?: string;
    approvedBy?: {
        id: string;
        name: string;
        email: string;
    } | null;
    rejectedAt?: string;
    rejectedByRole?: string;
    rejectedBy?: {
        id: string;
        name: string;
        email: string;
    } | null;
    auditTrail?: Array<{
        id: string;
        action: string;
        createdAt: string;
        user?: {
            id: string;
            name: string;
            email: string;
        } | null;
    }>;
}

interface StaffOption {
    id: string;
    name: string;
    email: string;
}

const londonDateInput = (value: string | Date) => {
    const date = new Date(value);
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(date);

    const part = (type: string) => parts.find(item => item.type === type)?.value || '';
    return `${part('year')}-${part('month')}-${part('day')}`;
};

const londonTimeInput = (value: string | Date) => {
    const date = new Date(value);
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(date);

    const part = (type: string) => parts.find(item => item.type === type)?.value || '';
    return `${part('hour')}:${part('minute')}`;
};

const displayDate = (value: string | Date) => new Date(value).toLocaleDateString('en-GB', {
    timeZone: 'Europe/London',
});

const displayTime = (value: string | Date) => new Date(value).toLocaleTimeString('en-GB', {
    timeZone: 'Europe/London',
    hour: '2-digit',
    minute: '2-digit',
});

const roleName = (role: unknown) => {
    if (!role) return '';
    if (typeof role === 'string') return role;
    if (typeof role === 'object' && 'name' in role) return String((role as { name?: string }).name || '');
    return '';
};

const isTimesheetManager = (role: unknown) => {
    const normalized = roleName(role).toUpperCase();
    return ['SUPER ADMIN', 'ADMIN', 'SYSTEM ADMINISTRATOR', 'ADMINISTRATOR', 'STATION MANAGER', 'OFFICE'].includes(normalized);
};

const auditActionLabel = (action: string) => {
    switch (action) {
        case 'TIMESHEET_ADMIN_CREATED': return 'Created';
        case 'TIMESHEET_ADMIN_UPDATED': return 'Amended';
        case 'TIMESHEET_ADMIN_DELETED': return 'Deleted';
        default: return action.replace(/^TIMESHEET_/, '').replace(/_/g, ' ').toLowerCase();
    }
};

const reviewAuditText = (ts: Timesheet) => {
    if (ts.approvedBy) {
        return `Approved by ${ts.approvedBy.name}${ts.approvedByRole ? ` (${ts.approvedByRole})` : ''}${ts.approvedAt ? ` · ${displayDate(ts.approvedAt)} ${displayTime(ts.approvedAt)}` : ''}`;
    }
    if (ts.rejectedBy) {
        return `Rejected by ${ts.rejectedBy.name}${ts.rejectedByRole ? ` (${ts.rejectedByRole})` : ''}${ts.rejectedAt ? ` · ${displayDate(ts.rejectedAt)} ${displayTime(ts.rejectedAt)}` : ''}`;
    }
    return '';
};

export const hasAdditionalTimesheetInfo = (ts: Pick<Timesheet, 'overtimeReason' | 'rejectionReason' | 'notes' | 'approvedBy' | 'rejectedBy' | 'auditTrail'>) => Boolean(
    ts.overtimeReason
    || ts.rejectionReason
    || ts.notes
    || ts.approvedBy
    || ts.rejectedBy
    || ts.auditTrail?.length
);

const TimesheetApproval = () => {
    const { user } = useAuth();
    const canManageTimesheets = isTimesheetManager(user?.role);
    const canDeleteTimesheets = isAdmin(user?.role);
    const [activeTab, setActiveTab] = useState<'pending' | 'history'>('pending');
    const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
    const [staff, setStaff] = useState<StaffOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [processingId, setProcessingId] = useState<string | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<Timesheet | null>(null);
    const [formError, setFormError] = useState('');
    const [formData, setFormData] = useState({
        userId: '',
        shiftDate: new Date().toISOString().split('T')[0],
        startTime: '09:00',
        endTime: '17:00',
        breakDuration: 0,
        totalHours: 8,
        status: 'APPROVED' as 'PENDING' | 'APPROVED' | 'REJECTED',
        notes: '',
        overtimeReason: '',
    });

    // Filters
    const [statusFilter, setStatusFilter] = useState('');
    const [dateFilter, setDateFilter] = useState('');

    useEffect(() => {
        fetchTimesheets();
    }, [activeTab, statusFilter, dateFilter]);

    useEffect(() => {
        if (!canManageTimesheets) return;
        apiClient.get('/users', { params: { limit: 500 } })
            .then(response => setStaff(response.data.data || []))
            .catch(error => console.error('Failed to load staff for timesheet admin', error));
    }, [canManageTimesheets]);

    useEffect(() => {
        const start = new Date(`${formData.shiftDate}T${formData.startTime}`);
        const end = new Date(`${formData.shiftDate}T${formData.endTime}`);
        if (end <= start) end.setDate(end.getDate() + 1);
        const total = Math.max(0, (end.getTime() - start.getTime()) / 36e5 - formData.breakDuration / 60);
        setFormData(prev => ({ ...prev, totalHours: Number(total.toFixed(2)) }));
    }, [formData.shiftDate, formData.startTime, formData.endTime, formData.breakDuration]);

    const fetchTimesheets = async () => {
        setLoading(true);
        try {
            let endpoint = '/timesheets';
            const params: any = {};

            if (activeTab === 'pending') {
                params.status = 'PENDING';
            } else {
                // History tab
                if (statusFilter) params.status = statusFilter;
                if (dateFilter) {
                    params.startDate = dateFilter;
                    params.endDate = dateFilter;
                }
            }

            const response = await apiClient.get(endpoint, { params });
            setTimesheets(response.data.data);
        } catch (error) {
            console.error('Failed to fetch timesheets:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAction = async (id: string, status: 'APPROVED' | 'REJECTED') => {
        let rejectionReason = '';
        if (status === 'REJECTED') {
            rejectionReason = prompt('Please enter a reason for rejection:') || '';
            if (!rejectionReason) return;
        }

        setProcessingId(id);
        try {
            await apiClient.put(`/timesheets/${id}/status`, { status, rejectionReason });
            showToast(`Timesheet ${status.toLowerCase()} successfully`, 'success');
            fetchTimesheets();
        } catch (error) {
            console.error(`Failed to ${status.toLowerCase()} timesheet:`, error);
            showToast(`Failed to ${status.toLowerCase()} timesheet`, 'error');
        } finally {
            setProcessingId(null);
        }
    };

    const openCreateModal = () => {
        setEditing(null);
        setFormError('');
        setFormData({
            userId: staff[0]?.id || '',
            shiftDate: new Date().toISOString().split('T')[0],
            startTime: '09:00',
            endTime: '17:00',
            breakDuration: 0,
            totalHours: 8,
            status: 'APPROVED',
            notes: '',
            overtimeReason: '',
        });
        setModalOpen(true);
    };

    const openEditModal = (timesheet: Timesheet) => {
        setEditing(timesheet);
        setFormError('');
        setFormData({
            userId: timesheet.user.id,
            shiftDate: londonDateInput(timesheet.shiftDate),
            startTime: londonTimeInput(timesheet.startTime),
            endTime: londonTimeInput(timesheet.endTime),
            breakDuration: timesheet.breakDuration || 0,
            totalHours: timesheet.totalHours,
            status: timesheet.status,
            notes: timesheet.notes || '',
            overtimeReason: timesheet.overtimeReason || '',
        });
        setModalOpen(true);
    };

    const submitAdminForm = async () => {
        setFormError('');
        if (!formData.userId) {
            setFormError('Select a staff member');
            return;
        }
        const start = new Date(`${formData.shiftDate}T${formData.startTime}`);
        const end = new Date(`${formData.shiftDate}T${formData.endTime}`);
        if (end <= start) end.setDate(end.getDate() + 1);

        const payload = {
            userId: formData.userId,
            shiftDate: new Date(`${formData.shiftDate}T00:00:00`).toISOString(),
            startTime: start.toISOString(),
            endTime: end.toISOString(),
            breakDuration: formData.breakDuration,
            totalHours: formData.totalHours,
            status: formData.status,
            notes: formData.notes || undefined,
            overtimeReason: formData.overtimeReason || undefined,
        };

        try {
            if (editing) {
                await apiClient.put(`/timesheets/${editing.id}`, payload);
                showToast('Timesheet updated', 'success');
            } else {
                await apiClient.post('/timesheets/admin', payload);
                showToast('Timesheet created', 'success');
            }
            setModalOpen(false);
            fetchTimesheets();
        } catch (error: any) {
            setFormError(error.response?.data?.error?.message || 'Failed to save timesheet');
        }
    };

    const deleteTimesheet = async (timesheet: Timesheet) => {
        if (!window.confirm(`Delete ${timesheet.user.name}'s timesheet for ${displayDate(timesheet.shiftDate)}?`)) return;
        setProcessingId(timesheet.id);
        try {
            await apiClient.delete(`/timesheets/${timesheet.id}`);
            showToast('Timesheet deleted', 'success');
            fetchTimesheets();
        } catch (error: any) {
            showToast(error.response?.data?.error?.message || 'Failed to delete timesheet', 'error');
        } finally {
            setProcessingId(null);
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'APPROVED': return <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-bold border border-green-200">APPROVED</span>;
            case 'REJECTED': return <span className="px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs font-bold border border-red-200">REJECTED</span>;
            default: return <span className="px-3 py-1 rounded-full bg-yellow-100 text-yellow-700 text-xs font-bold border border-yellow-200">PENDING</span>;
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">Timesheet Approvals</h1>
                    <p className="text-slate-500 mt-1">Manage and review staff hours</p>
                </div>

                <div className="flex flex-wrap gap-3 items-center">
                    {canManageTimesheets && (
                        <button onClick={openCreateModal} className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700">
                            + Create Timesheet
                        </button>
                    )}
                    {/* Tabs */}
                    <div className="bg-slate-100 p-1 rounded-xl inline-flex">
                        <button
                            onClick={() => setActiveTab('pending')}
                            className={`px-6 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === 'pending'
                                    ? 'bg-white text-blue-600 shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700'
                                }`}
                        >
                            Pending Review
                        </button>
                        <button
                            onClick={() => setActiveTab('history')}
                            className={`px-6 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === 'history'
                                    ? 'bg-white text-blue-600 shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700'
                                }`}
                        >
                            History
                        </button>
                    </div>
                </div>
            </div>

            {/* Filters for History */}
            {activeTab === 'history' && (
                <div className="bg-white p-4 rounded-xl border border-slate-200 flex flex-wrap gap-4 items-center">
                    <span className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Filters:</span>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-4 py-2 rounded-lg border border-slate-200 text-sm focus:border-blue-500 outline-none"
                    >
                        <option value="">All Statuses</option>
                        <option value="APPROVED">Approved</option>
                        <option value="REJECTED">Rejected</option>
                    </select>
                    <div className="w-44">
                        <DatePicker
                            value={dateFilter}
                            onChange={setDateFilter}
                            placeholder="Filter by date"
                        />
                    </div>
                    {(statusFilter || dateFilter) && (
                        <button
                            onClick={() => { setStatusFilter(''); setDateFilter(''); }}
                            className="text-sm text-red-500 hover:text-red-700 font-medium"
                        >
                            Clear Filters
                        </button>
                    )}
                </div>
            )}

            {/* Content */}
            <div className="grid gap-4">
                {loading ? (
                    <div className="space-y-3">
                        {[1,2,3].map(i => (
                            <div key={i} className="bg-white rounded-xl border border-slate-200 p-6 animate-pulse">
                                <div className="flex gap-4"><div className="w-12 h-12 bg-slate-200 rounded-full" /><div className="flex-1 space-y-2"><div className="h-4 bg-slate-200 rounded w-1/3"/><div className="h-3 bg-slate-100 rounded w-1/4"/></div></div>
                            </div>
                        ))}
                    </div>
                ) : timesheets.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
                        <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <svg className="w-6 h-6 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>
                        </div>
                        <p className="text-slate-600 font-semibold">{activeTab === 'pending' ? 'No pending timesheets' : 'No timesheets found'}</p>
                        <p className="text-sm text-slate-400 mt-1">{activeTab === 'pending' ? 'All timesheets are up to date.' : 'Try adjusting your filters.'}</p>
                    </div>
                ) : (
                    timesheets.map((ts) => {
                        const reviewAudit = reviewAuditText(ts);
                        const latestChange = ts.auditTrail?.[ts.auditTrail.length - 1];

                        return (
                        <div key={ts.id} className="bg-white rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition-shadow overflow-hidden">
                            <div className="p-5 flex flex-col xl:flex-row xl:items-center justify-between gap-5">
                                {/* User Info */}
                                <div className="flex items-center gap-4 min-w-[220px]">
                                    <div className="h-11 w-11 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-blue-500/20">
                                        {ts.user.name.charAt(0)}
                                    </div>
                                    <div className="min-w-0">
                                        <h3 className="font-bold text-slate-900 text-base truncate">{ts.user.name}</h3>
                                        <p className="text-xs text-slate-500 font-semibold bg-slate-100 px-2 py-0.5 rounded inline-block mt-1">
                                            {ts.user.role.name}
                                        </p>
                                    </div>
                                </div>

                                {/* Shift Details */}
                                <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                    <div>
                                        <p className="text-slate-400 font-semibold text-xs uppercase tracking-wide mb-1">Date</p>
                                        <p className="font-semibold text-slate-700">{displayDate(ts.shiftDate)}</p>
                                    </div>
                                    <div>
                                        <p className="text-slate-400 font-semibold text-xs uppercase tracking-wide mb-1">Time</p>
                                        <p className="font-semibold text-slate-700">
                                            {displayTime(ts.startTime)} - {displayTime(ts.endTime)}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-slate-400 font-semibold text-xs uppercase tracking-wide mb-1">Hours</p>
                                        <p className="font-bold text-blue-600 text-lg">{ts.totalHours} hrs</p>
                                    </div>
                                    <div>
                                        <p className="text-slate-400 font-semibold text-xs uppercase tracking-wide mb-1">Status</p>
                                        {getStatusBadge(ts.status)}
                                    </div>
                                </div>

                                {/* Actions (Only for Pending) */}
                                {ts.status === 'PENDING' && (
                                    <div className="flex flex-row md:flex-col gap-2 min-w-[140px]">
                                        <button
                                            onClick={() => handleAction(ts.id, 'APPROVED')}
                                            disabled={processingId === ts.id}
                                            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 shadow-lg shadow-green-500/20 transition-all disabled:opacity-50"
                                        >
                                            Approve
                                        </button>
                                        <button
                                            onClick={() => handleAction(ts.id, 'REJECTED')}
                                            disabled={processingId === ts.id}
                                            className="flex-1 px-4 py-2 bg-white border border-red-200 text-red-600 rounded-lg font-semibold hover:bg-red-50 transition-all disabled:opacity-50"
                                        >
                                            Reject
                                        </button>
                                    </div>
                                )}
                                {canManageTimesheets && (
                                    <div className="flex flex-row xl:flex-col gap-2 min-w-[110px]">
                                        <button
                                            onClick={() => openEditModal(ts)}
                                            disabled={processingId === ts.id}
                                            className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 disabled:opacity-50"
                                        >
                                            Edit
                                        </button>
                                        {canDeleteTimesheets && (
                                            <button
                                                onClick={() => deleteTimesheet(ts)}
                                                disabled={processingId === ts.id}
                                                className="px-4 py-2 bg-white border border-red-200 text-red-600 rounded-lg font-semibold hover:bg-red-50 disabled:opacity-50"
                                            >
                                                Delete
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Additional Info */}
                            {hasAdditionalTimesheetInfo(ts) && (
                                <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/70 space-y-2">
                                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-600">
                                        {reviewAudit && (
                                            <span>
                                                <span className="font-semibold text-slate-700">Review:</span> {reviewAudit}
                                            </span>
                                        )}
                                        {latestChange && (
                                            <span>
                                                <span className="font-semibold text-slate-700">Last change:</span> {auditActionLabel(latestChange.action)} by {latestChange.user?.name || 'System'} · {displayDate(latestChange.createdAt)} {displayTime(latestChange.createdAt)}
                                            </span>
                                        )}
                                    </div>
                                    {ts.overtimeReason && (
                                        <div className="rounded-lg border border-yellow-100 bg-yellow-50 px-3 py-2">
                                            <p className="text-xs font-bold text-yellow-700 uppercase tracking-wide mb-1">Overtime Reason</p>
                                            <p className="text-sm text-yellow-800">{ts.overtimeReason}</p>
                                        </div>
                                    )}
                                    {ts.rejectionReason && (
                                        <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2">
                                            <p className="text-xs font-bold text-red-700 uppercase tracking-wide mb-1">Rejection Reason</p>
                                            <p className="text-sm text-red-800">{ts.rejectionReason}</p>
                                        </div>
                                    )}
                                    {ts.notes && (
                                        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                                            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Notes</p>
                                            <p className="text-sm text-slate-600">{ts.notes}</p>
                                        </div>
                                    )}
                                    {!!ts.auditTrail?.length && ts.auditTrail.length > 1 && (
                                        <details className="text-sm">
                                            <summary className="cursor-pointer text-slate-500 hover:text-slate-700 font-semibold">View full change audit</summary>
                                            <div className="mt-2 space-y-1">
                                                {ts.auditTrail.map(log => (
                                                    <p key={log.id} className="text-slate-600">
                                                        {auditActionLabel(log.action)} by {log.user?.name || 'System'} · {displayDate(log.createdAt)} {displayTime(log.createdAt)}
                                                    </p>
                                                ))}
                                            </div>
                                        </details>
                                    )}
                                </div>
                            )}
                        </div>
                        );
                    })
                )}
            </div>
            {modalOpen && canManageTimesheets && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-bold text-slate-900">{editing ? 'Edit Timesheet' : 'Create Timesheet'}</h2>
                                <p className="text-sm text-slate-500">Admin/office-managed staff hours record</p>
                            </div>
                            <button onClick={() => setModalOpen(false)} className="text-slate-400 hover:text-slate-700 text-2xl leading-none">×</button>
                        </div>
                        <div className="p-6 space-y-4">
                            {formError && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{formError}</div>}
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">Staff</label>
                                <select value={formData.userId} onChange={e => setFormData({ ...formData, userId: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-slate-200">
                                    <option value="">Select staff</option>
                                    {staff.map(item => <option key={item.id} value={item.id}>{item.name} · {item.email}</option>)}
                                </select>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Date</label>
                                    <input type="date" value={formData.shiftDate} onChange={e => setFormData({ ...formData, shiftDate: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-slate-200" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Start</label>
                                    <input type="time" value={formData.startTime} onChange={e => setFormData({ ...formData, startTime: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-slate-200" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Finish</label>
                                    <input type="time" value={formData.endTime} onChange={e => setFormData({ ...formData, endTime: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-slate-200" />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Break minutes</label>
                                    <input type="number" min="0" value={formData.breakDuration} onChange={e => setFormData({ ...formData, breakDuration: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2.5 rounded-lg border border-slate-200" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Hours</label>
                                    <input type="number" step="0.01" min="0" value={formData.totalHours} onChange={e => setFormData({ ...formData, totalHours: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2.5 rounded-lg border border-slate-200" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Status</label>
                                    <select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value as any })} className="w-full px-3 py-2.5 rounded-lg border border-slate-200">
                                        <option value="APPROVED">Approved</option>
                                        <option value="PENDING">Pending</option>
                                        <option value="REJECTED">Rejected</option>
                                    </select>
                                </div>
                            </div>
                            <textarea value={formData.overtimeReason} onChange={e => setFormData({ ...formData, overtimeReason: e.target.value })} placeholder="Overtime reason" className="w-full px-3 py-2.5 rounded-lg border border-slate-200 min-h-[70px]" />
                            <textarea value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="Admin notes" className="w-full px-3 py-2.5 rounded-lg border border-slate-200 min-h-[70px]" />
                        </div>
                        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex gap-3 justify-end">
                            <button onClick={() => setModalOpen(false)} className="px-4 py-2 rounded-lg border border-slate-200 text-slate-700 font-semibold">Cancel</button>
                            <button onClick={submitAdminForm} className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700">{editing ? 'Save Changes' : 'Create'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TimesheetApproval;
